/**
 * Activities schemas.
 */
import { z } from 'zod';
import { activityTypeSchema, baseResponseSchema, createDataArrayResponseSchema, createDataResponseSchema, durationMinutesSchema, iso8601DateTimeSchema, latSchema, lonSchema, metricTypeSchema, timeRangeQuerySchema, } from "./common.js";
import { commentSchema } from "./notes.js";
import { hrZoneSecsSchema } from "./settings.js";
/**
 * Health Connect exercise types mapping.
 * Maps friendly names to Health Connect ExerciseSessionRecord type values.
 * @see https://developer.android.com/reference/kotlin/androidx/health/connect/client/records/ExerciseSessionRecord
 */
export const exerciseTypes = {
    back_extension: 1,
    badminton: 2,
    barbell_shoulder_press: 3,
    baseball: 4,
    basketball: 5,
    bench_press: 6,
    bench_sit_up: 7,
    biking: 8,
    biking_stationary: 9,
    boot_camp: 10,
    boxing: 11,
    burpee: 12,
    calisthenics: 13,
    cricket: 14,
    crunch: 15,
    dancing: 16,
    deadlift: 17,
    dumbbell_curl_left_arm: 18,
    dumbbell_curl_right_arm: 19,
    dumbbell_front_raise: 20,
    dumbbell_lateral_raise: 21,
    dumbbell_triceps_extension_left_arm: 22,
    dumbbell_triceps_extension_right_arm: 23,
    dumbbell_triceps_extension_two_arm: 24,
    elliptical: 25,
    exercise_class: 26,
    fencing: 27,
    football_american: 28,
    football_australian: 29,
    forward_twist: 30,
    frisbee_disc: 31,
    golf: 32,
    guided_breathing: 33,
    gymnastics: 34,
    handball: 35,
    high_intensity_interval_training: 36,
    hiking: 37,
    ice_hockey: 38,
    ice_skating: 39,
    jump_rope: 41,
    jumping_jack: 40,
    lat_pull_down: 42,
    lunge: 43,
    martial_arts: 44,
    other_workout: 0,
    paddling: 46,
    paragliding: 47,
    pilates: 48,
    plank: 49,
    racquetball: 50,
    rock_climbing: 51,
    roller_hockey: 52,
    rowing: 53,
    rowing_machine: 54,
    rugby: 55,
    running: 56,
    running_treadmill: 57,
    sailing: 58,
    scuba_diving: 59,
    skating: 60,
    skiing: 61,
    snowboarding: 62,
    snowshoeing: 63,
    soccer: 64,
    softball: 65,
    squash: 66,
    squat: 67,
    stair_climbing: 68,
    stair_climbing_machine: 69,
    strength_training: 70,
    stretching: 71,
    surfing: 72,
    swimming_open_water: 73,
    swimming_pool: 74,
    table_tennis: 75,
    tennis: 76,
    upper_twist: 77,
    volleyball: 78,
    walking: 79,
    water_polo: 80,
    weightlifting: 81,
    wheelchair: 82,
    yoga: 83,
};
export const exerciseTypeNames = Object.keys(exerciseTypes);
export const exerciseTypeSchema = z
    .enum(exerciseTypeNames)
    .meta({
    description: 'Exercise type name',
    example: 'weightlifting',
    id: 'ExerciseTypeName',
});
export const isValidExerciseType = (name) => name in exerciseTypes;
/**
 * True when an activity_type counts as a workout/exercise — either the generic
 * `'exercise'` bucket or one of the Health Connect exercise subtypes (running,
 * cycling, …). Used by widgets and summaries that count "workouts".
 */
export const isExerciseActivityType = (activityType) => activityType === 'exercise' || activityType in exerciseTypes;
/** Reverse lookup: Health Connect exercise type integer → exercise type name. */
const exerciseTypesByValue = Object.fromEntries(Object.entries(exerciseTypes).map(([name, value]) => [value, name]));
/** Get the exercise type name from its Health Connect integer value, or undefined if unknown. */
export const getExerciseTypeName = (value) => exerciseTypesByValue[value];
export const getExerciseTypeValue = (name) => exerciseTypes[name];
/**
 * Computed/extracted summary metrics for an activity.
 *
 * These are surface-level aggregates derived from per-second time-series data
 * and/or summary fields stored in `data` JSONB. They are data-source-agnostic:
 * any source that populates the underlying time-series (Garmin, Strava,
 * Health Connect, FIT upload, etc.) will produce these values.
 */
export const activitySummaryMetricsSchema = z
    .object({
    avg_cadence: z.number().optional().meta({ description: 'Average run cadence (spm)' }),
    avg_ground_contact_time: z.number().optional().meta({ description: 'Average ground contact time (ms)' }),
    avg_hr: z.number().optional().meta({ description: 'Average heart rate (bpm)' }),
    avg_pace: z.number().optional().meta({ description: 'Average pace (seconds per kilometer)' }),
    avg_power: z.number().optional().meta({ description: 'Average power (W)' }),
    avg_speed: z.number().optional().meta({ description: 'Average speed (m/s)' }),
    avg_stride_length: z.number().optional().meta({ description: 'Average stride length (m)' }),
    body_battery_after: z
        .number()
        .optional()
        .meta({ description: 'Body battery score at activity end (0-100)' }),
    body_battery_before: z
        .number()
        .optional()
        .meta({ description: 'Body battery score at activity start (0-100)' }),
    calories: z.number().optional().meta({ description: 'Total calories burned (kcal)' }),
    distance: z.number().optional().meta({ description: 'Total distance (m)' }),
    elevation_gain: z.number().optional().meta({ description: 'Total elevation gain (m)' }),
    elevation_loss: z.number().optional().meta({ description: 'Total elevation loss (m)' }),
    max_hr: z.number().optional().meta({ description: 'Maximum heart rate (bpm)' }),
    steps: z.number().optional().meta({ description: 'Total step count' }),
    vo2_max: z.number().optional().meta({ description: 'VO2 max (mL/kg/min)' }),
})
    .meta({
    description: 'Summary metrics derived from per-second time-series and stored summary fields. Fields are populated only when underlying data is available.',
    id: 'ActivitySummaryMetrics',
});
/**
 * All metrics computed for an activity at query time: HR-zone seconds,
 * average HRV, and the generic summary metrics. Returned by both the
 * single-activity detail endpoint and embedded into list-query results.
 *
 * "Computed" = derived from time-series and/or activity.data, not stored
 * directly on the activities row.
 */
export const activityComputedMetricsSchema = activitySummaryMetricsSchema
    .extend({
    avg_hrv: z.number().optional().meta({ description: 'Average HRV (ms) during the activity' }),
    hr_zone_secs: hrZoneSecsSchema.optional().meta({
        description: 'Time spent in each HR zone (for exercise)',
    }),
})
    .meta({ id: 'ActivityComputedMetrics' });
/**
 * Activity schema.
 */
export const activitySchema = activityComputedMetricsSchema
    .extend({
    activity_type: z.string().meta({ description: 'Activity type' }),
    comments: z.array(commentSchema).optional().meta({ description: 'Attached comments' }),
    data: z.record(z.string(), z.unknown()).optional(),
    deleted_at: iso8601DateTimeSchema.optional().meta({ description: 'Soft-delete timestamp' }),
    duration: durationMinutesSchema.optional(),
    end_time: iso8601DateTimeSchema.optional(),
    external_id: z.string().optional().meta({ description: 'External ID from source system' }),
    id: z.string().uuid().optional().meta({ description: 'Activity ID' }),
    override_target_ids: z.array(z.string().uuid()).optional().meta({
        description: 'When non-empty, this activity is a user-edited aurboda override that hides the listed synced activities in merged views (#715, #735). One override may target multiple synced rows when claiming a cross-source merge group as a whole. Each target may be overridden by at most one override.',
    }),
    source: z.string().optional().meta({ description: 'Data source' }),
    start_time: iso8601DateTimeSchema,
    time_in_bed: durationMinutesSchema.optional().meta({
        description: 'Time in bed in minutes (end_time - start_time). Only present for sleep activities.',
    }),
    title: z.string().optional().meta({ description: 'Activity title' }),
    total_sleep: durationMinutesSchema.optional().meta({
        description: 'Actual sleep time in minutes, excluding awake periods. Only present for sleep activities with stage data.',
    }),
})
    .meta({ id: 'Activity' });
/**
 * Source record schema for multi-source merged activities.
 */
export const sourceRecordSchema = z
    .object({
    activity_type: z.string().optional().meta({ description: 'Activity type (e.g. yoga, running)' }),
    data_origin: z.string().optional().meta({ description: 'Health Connect data origin package' }),
    end_time: iso8601DateTimeSchema.optional(),
    id: z.string().uuid().meta({ description: 'Activity ID' }),
    source: z.string().meta({ description: 'Data source' }),
    start_time: iso8601DateTimeSchema,
    title: z.string().optional().meta({ description: 'Activity title' }),
})
    .meta({ description: 'Individual source record within a merged activity', id: 'SourceRecord' });
/**
 * Activity detail response schema (single activity with optional merge info).
 */
export const activityDetailSchema = activitySchema
    .extend({
    merged_end_time: iso8601DateTimeSchema
        .optional()
        .meta({ description: 'Merged end time across all overlapping sources' }),
    merged_start_time: iso8601DateTimeSchema
        .optional()
        .meta({ description: 'Merged start time across all overlapping sources' }),
    source_records: z
        .array(sourceRecordSchema)
        .optional()
        .meta({ description: 'Individual source records when activity is merged from multiple sources' }),
})
    .meta({ id: 'ActivityDetail' });
/**
 * Activities query schema.
 */
export const activitiesQuerySchema = timeRangeQuerySchema
    .extend({
    types: z.string().optional().meta({
        description: 'Comma-separated activity types to include',
        example: 'sleep,exercise',
    }),
    exclude_types: z.string().optional().meta({
        description: 'Comma-separated activity types to exclude',
        example: 'sleep,exercise,nap,rest',
    }),
    data_filter: z.string().optional().meta({
        description: 'Filter by JSONB data field values. Format: "field:value" (comma-separated for multiple). Use "(none)" for null/empty.',
        example: 'partner:Sara',
    }),
    deduction_rule_id: z.string().uuid().optional().meta({
        description: 'Filter to activities created or enriched by a specific deduction rule',
    }),
})
    .meta({ id: 'ActivitiesQuery' });
/**
 * Activities response schema.
 */
export const activitiesResponseSchema = createDataArrayResponseSchema(activitySchema).meta({
    id: 'ActivitiesResponse',
});
/**
 * Add activity request body schema.
 */
export const addActivityBodySchema = z
    .object({
    activity_type: activityTypeSchema.meta({ description: 'Type of activity' }),
    data: z.record(z.string(), z.unknown()).optional().meta({
        description: 'Structured data for this activity (type-specific fields)',
    }),
    end_time: iso8601DateTimeSchema.optional().meta({
        description: 'End time (omit for point-in-time activities)',
    }),
    merge_span: z.number().int().positive().max(3600).optional().meta({
        description: 'If provided, merge with existing activity of same type if its end_time is within this many seconds of new start_time. Max 3600.',
    }),
    notes: z.string().optional().meta({
        description: 'Activity notes. For workouts, use format: "Exercise Name: reps×weight, reps×weight" per line.',
    }),
    start_time: iso8601DateTimeSchema.meta({ description: 'Start time of the activity' }),
    title: z
        .string()
        .optional()
        .meta({ description: 'Activity title (e.g., "Upper body", "Morning meditation")' }),
})
    .meta({ id: 'AddActivityBody' });
/**
 * Added activity data schema.
 */
const addedActivitySchema = z.object({
    activity_type: activityTypeSchema,
    end_time: iso8601DateTimeSchema.optional(),
    id: z.string().uuid(),
    start_time: iso8601DateTimeSchema,
    title: z.string().optional(),
});
/**
 * Add activity response schema.
 */
export const addActivityResponseSchema = baseResponseSchema
    .extend({
    data: addedActivitySchema.optional(),
})
    .meta({ id: 'AddActivityResponse' });
/**
 * Delete activity params.
 */
export const deleteActivityParamsSchema = z
    .object({
    id: z.string().uuid().meta({ description: 'ID of the activity to delete' }),
})
    .meta({ id: 'DeleteActivityParams' });
/**
 * Delete activity response.
 */
export const deleteActivityResponseSchema = baseResponseSchema.meta({ id: 'DeleteActivityResponse' });
/**
 * Update activity request body schema.
 * All fields are optional - only provided fields will be updated.
 */
export const updateActivityBodySchema = z
    .object({
    activity_type: activityTypeSchema.optional().meta({ description: 'New activity type' }),
    data: z.record(z.string(), z.unknown()).optional().meta({
        description: 'Updated structured data fields (merged with existing data)',
    }),
    end_time: iso8601DateTimeSchema
        .nullable()
        .optional()
        .meta({ description: 'New end time of the activity (null to clear)' }),
    notes: z.string().optional().meta({ description: 'New activity notes' }),
    override_target_ids: z.array(z.string().uuid()).min(1).optional().meta({
        description: 'When the PATCH targets a synced activity from a merged view, the caller passes the full list of source ids the new override should claim. Defaults to just the targeted activity (single-target case).',
    }),
    start_time: iso8601DateTimeSchema.optional().meta({ description: 'New start time of the activity' }),
    title: z.string().optional().meta({ description: 'New activity title' }),
})
    .meta({ id: 'UpdateActivityBody' });
/**
 * Update activity params.
 */
export const updateActivityParamsSchema = z
    .object({
    id: z.string().uuid().meta({ description: 'ID of the activity to update' }),
})
    .meta({ id: 'UpdateActivityParams' });
/**
 * Update activity response schema.
 */
export const updateActivityResponseSchema = baseResponseSchema
    .extend({
    data: addedActivitySchema.optional(),
})
    .meta({ id: 'UpdateActivityResponse' });
/**
 * Merge activities request body schema.
 */
export const mergeActivitiesBodySchema = z
    .object({
    activity_ids: z
        .array(z.string().uuid())
        .min(2)
        .meta({ description: 'IDs of activities to merge (minimum 2)' }),
    notes: z.string().optional().meta({ description: 'Optional notes override for the merged activity' }),
    title: z.string().optional().meta({ description: 'Optional title override for the merged activity' }),
})
    .meta({ id: 'MergeActivitiesBody', description: 'Request body for merging multiple activities into one' });
/**
 * Merge activities response schema.
 */
export const mergeActivitiesResponseSchema = baseResponseSchema
    .extend({
    data: activitySchema.optional(),
})
    .meta({ id: 'MergeActivitiesResponse' });
/**
 * Nearby activities query schema.
 */
export const nearbyActivitiesQuerySchema = z
    .object({
    hours: z.coerce
        .number()
        .optional()
        .default(6)
        .meta({ description: 'Hours before/after to search for nearby activities' }),
})
    .meta({ id: 'NearbyActivitiesQuery' });
/**
 * Nearby activities response schema.
 */
export const nearbyActivitiesResponseSchema = createDataArrayResponseSchema(activitySchema).meta({
    id: 'NearbyActivitiesResponse',
});
/**
 * Single activity detail response (for activity detail page).
 * Includes referenced_rules map for resolving rule IDs to names.
 */
export const activityDetailResponseSchema = createDataResponseSchema(activitySchema)
    .extend({
    referenced_rules: z
        .record(z.string(), z.string())
        .optional()
        .meta({ description: 'Map of rule ID to rule name for deduction rules referenced in activity data' }),
})
    .meta({
    id: 'ActivityDetailResponse',
});
/**
 * Re-sync activity detail response.
 */
export const resyncActivityDetailResponseSchema = baseResponseSchema
    .extend({
    points: z.number().int().meta({ description: 'Number of detail data points synced' }),
})
    .meta({ id: 'ResyncActivityDetailResponse' });
/**
 * Sentinel value for the activity full-detail `metrics` parameter that means
 * "every metric with data in the activity range". Defined as a constant so the
 * schema description, parser, and clients all agree.
 */
export const ALL_METRICS_SENTINEL = 'all';
/**
 * Query parameters for the activity full-detail endpoint.
 *
 * Lets callers opt in/out of expensive payload pieces (GPS, time-series) and
 * restrict to specific metrics to keep responses manageable.
 */
export const activityFullDetailQuerySchema = z
    .object({
    include_gps: z.coerce.boolean().optional().default(true).meta({
        description: 'Include GPS trace if any locations exist for the activity time range. Default: true.',
    }),
    metrics: z
        .string()
        .optional()
        .meta({
        description: `Comma-separated metric names to include time-series for, or '${ALL_METRICS_SENTINEL}' for every metric with data. Omit for summary + GPS only.`,
        example: 'heart_rate,speed,run_cadence',
    }),
})
    .meta({ id: 'ActivityFullDetailQuery' });
/**
 * GPS trace point for an activity.
 */
export const activityGpsPointSchema = z
    .object({
    lat: latSchema,
    lon: lonSchema,
    time: iso8601DateTimeSchema,
})
    .meta({ id: 'ActivityGpsPoint' });
/**
 * Per-metric time-series for an activity.
 *
 * `data` is a packed array of [time, value] tuples sorted by time.
 * If `bucket` was specified in the query the values are bucketed averages.
 */
export const activityMetricSeriesSchema = z
    .object({
    bucket: z.string().optional().meta({ description: 'Bucket size used (omitted for raw samples).' }),
    count: z.number().int().meta({ description: 'Number of points in the series' }),
    data: z
        .array(z.tuple([iso8601DateTimeSchema, z.number()]))
        .meta({ description: 'Array of [time, value] tuples sorted by time' }),
    metric: metricTypeSchema.or(z.string()).meta({ description: 'Metric name' }),
    unit: z.string().meta({ description: 'Unit of measurement' }),
})
    .meta({ id: 'ActivityMetricSeries' });
/**
 * Full activity detail with optional GPS trace and per-metric time-series.
 *
 * Designed for deep-dive analysis (route comparison, form analysis,
 * pace-by-grade studies). Lean fields (summary metrics, HR zones, comments)
 * are inherited from the regular activity-detail schema.
 */
export const activityFullDetailSchema = activityDetailSchema
    .extend({
    gps: z.array(activityGpsPointSchema).optional().meta({
        description: 'GPS trace covering the activity time range, if any locations are available.',
    }),
    metric_series: z.array(activityMetricSeriesSchema).optional().meta({
        description: 'Per-metric time-series during the activity time range.',
    }),
})
    .meta({ id: 'ActivityFullDetail' });
/**
 * Response for the activity full-detail endpoint.
 */
export const activityFullDetailResponseSchema = createDataResponseSchema(activityFullDetailSchema)
    .extend({
    referenced_rules: z
        .record(z.string(), z.string())
        .optional()
        .meta({ description: 'Map of rule ID to rule name for deduction rules referenced in activity data' }),
})
    .meta({ id: 'ActivityFullDetailResponse' });
//# sourceMappingURL=activities.js.map